import axios, { AxiosResponse, AxiosRequestConfig } from 'axios'
import { toast } from "@/hooks/use-toast"
import { getCurrentUser, setCurrentUser, removeCurrentUser } from '@/utils/session'

interface User {
  userid?: string
  access_token?: string
  refresh_token?: string
}

interface ApiResponse<T = unknown> {
  data: T;
  code?: number;
  message?: string;
}

interface CustomRequestConfig extends AxiosRequestConfig {
  errorMessage?: string;
}

const api = axios.create({
  baseURL: '/kinet3',
})

// Add request interceptor to automatically add token
api.interceptors.request.use(
  async (config) => {
    const user = getCurrentUser()
    if (user?.access_token) {
      config.headers.Authorization = `Bearer ${user.access_token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

let isRefreshing = false
let refreshSubscribers: ((token: string) => void)[] = []

// Add a subscriber to the queue
const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb)
}

// Execute all subscribers
const onRrefreshed = (token: string) => {
  refreshSubscribers.map(cb => cb(token))
  refreshSubscribers = []
}

const refreshToken = async (): Promise<string> => {
  const current: User = getCurrentUser() ?? {}
  
  if (!current.userid || !current.refresh_token) {
    toast({ variant: "destructive", title: "Login expired, please login again" })
    removeCurrentUser()
    window.location.href = '/login'
    throw new Error('No refresh token')
  }

  try {
    const response: AxiosResponse<User> = await axios.post('/kinet3/api/user/refresh_token', {
      user_id: current.userid,
      refresh_token: current.refresh_token,
    })
    const user = response.data
    if (user.access_token) {
      setCurrentUser({ access_token: user.access_token })
      if (user.refresh_token) {
        setCurrentUser({ refresh_token: user.refresh_token })
      }
      return user.access_token
    }
    throw new Error('No access token in response')
  } catch (error) {
    console.error('Failed to refresh token:', error)
    removeCurrentUser()
    window.location.href = '/login'
    throw error
  }
}

api.interceptors.response.use(
  async (response: AxiosResponse): Promise<any> => response.data,
  async (error: any) => {
    const { response, config }: { response: AxiosResponse<ApiResponse<unknown>>, config: CustomRequestConfig } = error
    const originalRequest = config

    // If refresh token request fails, redirect to login
    if (config.url === '/refresh_token') {
      toast({ variant: "destructive", title: "Operation timeout, please login again" })
      removeCurrentUser()
      window.location.href = '/login'
      return Promise.reject(error)
    }

    if (config.url === '/logout') {
      return Promise.reject(error)
    }

    // Token expired
    if (response?.status === 510 && response.data.code === 205) {
      if (!isRefreshing) {
        isRefreshing = true
        
        try {
          const newToken = await refreshToken()
          isRefreshing = false
          onRrefreshed(newToken)
          
          // Retry current request
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api.request(originalRequest);
        } catch (err) {
          isRefreshing = false
          return Promise.reject(err)
        }
      } else {
        // Return a promise waiting for token refresh
        return new Promise((resolve) => {
          subscribeTokenRefresh((token: string) => {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api.request(originalRequest));
          })
        })
      }
    } else if (response?.status === 510) {
      if (config.errorMessage) {
        toast({ variant: "destructive", title: config.errorMessage })
      } else {
        toast({ variant: "destructive", title: response.data.message })
      }
      return Promise.reject(error)
    } else {
      toast({ variant: "destructive", title: "API request failed" })
      return Promise.reject(error)
    }
  }
)

export default api
